import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const MinimoTributable = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [filtroNumeroLae, setFiltroNumeroLae] = useState("");
  const [filtroRif, setFiltroRif] = useState("");
  const [contribuyentesLae, setContribuyentesLae] = useState([]);
  const [openLaeCombobox, setOpenLaeCombobox] = useState(false);
  const [openRifCombobox, setOpenRifCombobox] = useState(false);

  useEffect(() => { fetchMinimoTributableData(); fetchContribuyentesLae(); }, []);

  const fetchContribuyentesLae = async () => {
    try {
      const response = await axios.get(`${API}/contribuyentes/con-lae`, { headers });
      setContribuyentesLae(response.data || []);
    } catch (error) {
      console.error("Error al cargar contribuyentes:", error);
    }
  };

  const fetchMinimoTributableData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      let url = `${API}/reportes/minimo-tributable-lae`;
      const params = new URLSearchParams();
      
      if (aplicarFiltros || filtroFechaDesde || filtroFechaHasta || filtroNumeroLae || filtroRif) {
        if (filtroFechaDesde) params.append('fecha_desde', filtroFechaDesde);
        if (filtroFechaHasta) params.append('fecha_hasta', filtroFechaHasta);
        if (filtroNumeroLae) params.append('numero_lae', filtroNumeroLae);
        if (filtroRif) params.append('rif', filtroRif);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      
      if (aplicarFiltros && params.toString()) {
        toast.success("Filtros aplicados correctamente");
      }
    } catch (error) {
      console.error("Error al cargar reporte:", error);
      toast.error("Error al cargar el reporte de mínimo tributable");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosMinimoTributable = () => {
    setFiltroFechaDesde("");
    setFiltroFechaHasta("");
    setFiltroNumeroLae("");
    setFiltroRif("");
    fetchMinimoTributableData(false);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosMinimoActivos = filtroFechaDesde || filtroFechaHasta || filtroNumeroLae || filtroRif;

  // Filtrar declaraciones mínimo tributable por búsqueda
  const filteredDeclaracionesMinimo = reporteData?.declaraciones?.filter(d => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      d.rif?.toLowerCase().includes(search) ||
      d.nombre?.toLowerCase().includes(search) ||
      d.actividad_economica?.toLowerCase().includes(search)
    );
  }) || [];

  // Exportar Mínimo Tributable a Excel
  const exportMinimoTributableToExcel = () => {
    if (!reporteData) return;
    
    const wsData = [
      ["REPORTE DE MÍNIMO TRIBUTABLE - CONTRIBUYENTES CON LAE"],
      [`Fecha de generación: ${new Date(reporteData.fecha_generacion).toLocaleString('es-VE')}`],
      [`Valor Unidad de Cuenta (Activa): Bs. ${reporteData.valor_unidad_cuenta_activa || 0}`],
      [],
      ["RESUMEN"],
      ["Total Declaraciones", reporteData.resumen?.total_declaraciones || 0],
      ["Declaraciones con Mínimo Tributable", reporteData.resumen?.declaraciones_minimo_tributable || 0],
      ["Declaraciones Normales", reporteData.resumen?.declaraciones_normales || 0],
      ["Porcentaje Mínimo Tributable", `${reporteData.resumen?.porcentaje_minimo || 0}%`],
      [],
      ["DETALLE DE DECLARACIONES CON MÍNIMO TRIBUTABLE APLICADO"],
      ["Nro. LAE", "RIF", "Nombre/Razón Social", "Actividad Económica", "Ingreso Bruto", "Alícuota %", "Imp. Calculado", "Multiplicador", "Mín. Aplicado (Bs.)", "Mes/Año"]
    ];
    
    (reporteData.declaraciones || []).forEach(d => {
      wsData.push([
        d.numero_lae || '',
        d.rif,
        d.nombre,
        d.actividad_economica,
        d.ingreso_bruto,
        d.alicuota,
        d.impuesto_calculado,
        d.minimo_multiplicador,
        d.minimo_tributable,
        d.mes_anio
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 12 }, { wch: 15 }, { wch: 35 }, { wch: 40 }, { wch: 15 }, 
      { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 12 }
    ];
    
    XLSX.utils.book_append_sheet(wb, ws, "MinimoTributable");
    XLSX.writeFile(wb, `Reporte_MinimoTributable_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Reporte exportado a Excel exitosamente");
  };

  // Exportar Mínimo Tributable a PDF
  const exportMinimoTributableToPDF = () => {
    if (!reporteData) return;
    
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175);
    doc.text("REPORTE DE MÍNIMO TRIBUTABLE - CONTRIBUYENTES CON LAE", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Fecha de generación: ${new Date(reporteData.fecha_generacion).toLocaleString('es-VE')}`, 14, 28);
    doc.text(`Valor Unidad de Cuenta (Activa): Bs. ${(reporteData.valor_unidad_cuenta_activa || 0).toLocaleString('es-VE')}`, 14, 34);
    
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text("RESUMEN", 14, 46);
    
    doc.setFontSize(10);
    doc.text(`Total Declaraciones: ${reporteData.resumen?.total_declaraciones || 0}`, 14, 54);
    doc.text(`Con Mínimo Tributable Aplicado: ${reporteData.resumen?.declaraciones_minimo_tributable || 0}`, 14, 60);
    doc.text(`Porcentaje: ${reporteData.resumen?.porcentaje_minimo || 0}%`, 14, 66);
    
    const tableData = (reporteData.declaraciones || []).map(d => [
      d.numero_lae || '-',
      d.rif,
      d.nombre?.substring(0, 18) || '',
      d.actividad_economica?.substring(0, 22) || '',
      `Bs. ${(d.ingreso_bruto || 0).toLocaleString('es-VE')}`,
      `${(d.alicuota || 0)}%`,
      `Bs. ${(d.impuesto_calculado || 0).toLocaleString('es-VE')}`,
      `×${d.minimo_multiplicador || 0}`,
      `Bs. ${(d.minimo_tributable || 0).toLocaleString('es-VE')}`,
      d.mes_anio
    ]);
    
    doc.autoTable({
      startY: 76,
      head: [['LAE', 'RIF', 'Nombre', 'Actividad', 'Ing. Bruto', 'Alíc.', 'Imp. Calc.', 'Mult.', 'Mín. Aplic.', 'Mes/Año']],
      body: tableData,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [217, 119, 6], textColor: 255 },
      alternateRowStyles: { fillColor: [254, 243, 199] },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 24 },
        2: { cellWidth: 32 },
        3: { cellWidth: 40 },
        4: { cellWidth: 22 },
        5: { cellWidth: 14 },
        6: { cellWidth: 22 },
        7: { cellWidth: 14 },
        8: { cellWidth: 24 },
        9: { cellWidth: 18 }
      }
    });
    
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `SIRET - Sistema de Recaudación Tributaria Municipal | Página ${i} de ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`Reporte_MinimoTributable_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Reporte exportado a PDF exitosamente");
  };

  return (
    <div className="space-y-6" data-testid="reporte-minimo-tributable">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mínimo Tributable - LAE
            </h1>
            <p className="text-slate-500">Contribuyentes con LAE que declararon mínimo tributable</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={mostrarFiltros ? "default" : "outline"} 
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={mostrarFiltros ? "bg-amber-600 hover:bg-amber-700" : ""}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
            {hayFiltrosMinimoActivos && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[filtroFechaDesde, filtroFechaHasta, filtroNumeroLae, filtroRif].filter(Boolean).length}
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={() => fetchMinimoTributableData(false)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button variant="outline" onClick={exportMinimoTributableToExcel} disabled={!reporteData}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" onClick={exportMinimoTributableToPDF} disabled={!reporteData}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Panel de Filtros */}
      {mostrarFiltros && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {/* Filtro por Número de LAE - Combobox con búsqueda */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Nro. LAE
                </Label>
                <Popover open={openLaeCombobox} onOpenChange={setOpenLaeCombobox}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openLaeCombobox}
                      className="w-full justify-between bg-white font-normal"
                      data-testid="filtro-numero-lae"
                    >
                      {filtroNumeroLae 
                        ? contribuyentesLae.find(c => c.numero_lae === filtroNumeroLae)
                          ? `${filtroNumeroLae} - ${(contribuyentesLae.find(c => c.numero_lae === filtroNumeroLae)?.nombre || '').substring(0, 15)}`
                          : filtroNumeroLae
                        : "Buscar LAE..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar por número de LAE..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron resultados.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="todos"
                            onSelect={() => {
                              setFiltroNumeroLae("");
                              setFiltroRif("");
                              setOpenLaeCombobox(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${!filtroNumeroLae ? "opacity-100" : "opacity-0"}`} />
                            Todos los LAE
                          </CommandItem>
                          {contribuyentesLae.filter(c => c.numero_lae).map((contrib) => (
                            <CommandItem
                              key={contrib.id}
                              value={`${contrib.numero_lae} ${contrib.nombre || contrib.razon_social || ''}`}
                              onSelect={() => {
                                setFiltroNumeroLae(contrib.numero_lae);
                                setFiltroRif(contrib.rif || "");
                                setOpenLaeCombobox(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${filtroNumeroLae === contrib.numero_lae ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-medium text-amber-700">{contrib.numero_lae}</span>
                              <span className="ml-2 text-slate-600 truncate">
                                - {(contrib.nombre || contrib.razon_social || '').substring(0, 25)}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Filtro por RIF/Contribuyente - Combobox con búsqueda */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  RIF / Contribuyente
                </Label>
                <Popover open={openRifCombobox} onOpenChange={setOpenRifCombobox}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openRifCombobox}
                      className="w-full justify-between bg-white font-normal"
                      data-testid="filtro-rif"
                    >
                      {filtroRif 
                        ? contribuyentesLae.find(c => c.rif === filtroRif)
                          ? `${contribuyentesLae.find(c => c.rif === filtroRif)?.tipo_rif || ''}-${filtroRif}`
                          : filtroRif
                        : "Buscar RIF..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar por RIF o nombre..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron resultados.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="todos"
                            onSelect={() => {
                              setFiltroRif("");
                              setFiltroNumeroLae("");
                              setOpenRifCombobox(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${!filtroRif ? "opacity-100" : "opacity-0"}`} />
                            Todos los contribuyentes
                          </CommandItem>
                          {contribuyentesLae.filter(c => c.rif).map((contrib) => (
                            <CommandItem
                              key={contrib.id}
                              value={`${contrib.tipo_rif || ''}-${contrib.rif} ${contrib.nombre || contrib.razon_social || ''}`}
                              onSelect={() => {
                                setFiltroRif(contrib.rif);
                                if (contrib.numero_lae) {
                                  setFiltroNumeroLae(contrib.numero_lae);
                                }
                                setOpenRifCombobox(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${filtroRif === contrib.rif ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-medium text-blue-700">{contrib.tipo_rif}-{contrib.rif}</span>
                              <span className="ml-2 text-slate-600 truncate">
                                - {(contrib.nombre || contrib.razon_social || '').substring(0, 22)}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Filtro por Fecha Desde */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Desde
                </Label>
                <Input
                  type="date"
                  value={filtroFechaDesde}
                  onChange={(e) => setFiltroFechaDesde(e.target.value)}
                  className="bg-white"
                  data-testid="filtro-fecha-desde"
                />
              </div>
              
              {/* Filtro por Fecha Hasta */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Hasta
                </Label>
                <Input
                  type="date"
                  value={filtroFechaHasta}
                  onChange={(e) => setFiltroFechaHasta(e.target.value)}
                  className="bg-white"
                  data-testid="filtro-fecha-hasta"
                />
              </div>
            </div>
            
            {/* Botones de acción */}
            <div className="flex gap-2 justify-end border-t border-amber-200 pt-4">
              <Button 
                onClick={() => fetchMinimoTributableData(true)} 
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Search className="w-4 h-4 mr-2" />
                Aplicar Filtros
              </Button>
              {hayFiltrosMinimoActivos && (
                <Button variant="outline" onClick={limpiarFiltrosMinimoTributable} disabled={loading}>
                  <X className="w-4 h-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </div>
            
            {/* Info de filtros aplicados */}
            {(filtroNumeroLae || filtroRif) && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-xs text-slate-600">
                  <strong>Histórico de:</strong>{" "}
                  {filtroNumeroLae && <span className="text-amber-700">LAE {filtroNumeroLae}</span>}
                  {filtroNumeroLae && filtroRif && " | "}
                  {filtroRif && <span className="text-amber-700">RIF {filtroRif}</span>}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas de resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Declaraciones</p>
                    <p className="text-3xl font-bold text-slate-900">{reporteData.resumen?.total_declaraciones || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Con Mínimo Tributable</p>
                    <p className="text-3xl font-bold text-amber-600">{reporteData.resumen?.declaraciones_minimo_tributable || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">% Mínimo Tributable</p>
                    <p className="text-3xl font-bold text-orange-600">{reporteData.resumen?.porcentaje_minimo || 0}%</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Declaraciones Normales</p>
                    <p className="text-3xl font-bold text-green-600">{reporteData.resumen?.declaraciones_normales || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Gráfico de barras - Distribución por Actividad */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Actividad Económica</CardTitle>
                <CardDescription>Cantidad de declaraciones con mínimo tributable por actividad</CardDescription>
              </CardHeader>
              <CardContent>
                {reporteData.distribucion_por_actividad?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={reporteData.distribucion_por_actividad.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="actividad" type="category" width={150} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => [value, 'Declaraciones']} />
                      <Bar dataKey="cantidad" name="Cantidad" fill="#D97706" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    No hay datos para mostrar
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gráfico circular - Comparativa */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mínimo Tributable vs Normales</CardTitle>
                <CardDescription>Proporción de declaraciones con mínimo tributable</CardDescription>
              </CardHeader>
              <CardContent>
                {(reporteData.resumen?.declaraciones_minimo_tributable > 0 || reporteData.resumen?.declaraciones_normales > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Mínimo Tributable', value: reporteData.resumen?.declaraciones_minimo_tributable || 0 },
                          { name: 'Normales', value: reporteData.resumen?.declaraciones_normales || 0 }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#D97706" />
                        <Cell fill="#22C55E" />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-slate-400">
                    No hay datos para mostrar
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de tendencia mensual */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tendencia Mensual</CardTitle>
              <CardDescription>Evolución de declaraciones con mínimo tributable vs normales por mes</CardDescription>
            </CardHeader>
            <CardContent>
              {reporteData.tendencia_mensual?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reporteData.tendencia_mensual}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="cantidad" name="Mínimo Tributable" fill="#D97706" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="normales" name="Normales" fill="#22C55E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-400">
                  No hay datos de tendencia mensual
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabla de declaraciones */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Declaraciones</CardTitle>
                  <CardDescription>Lista de declaraciones donde se aplicó el mínimo tributable como piso</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <Input
                    placeholder="Buscar por RIF, nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Info de Unidad de Cuenta */}
              {reporteData.valor_unidad_cuenta_activa && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Valor Unidad de Cuenta (Activa):</strong> Bs. {reporteData.valor_unidad_cuenta_activa.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                    <span className="ml-2 text-xs text-blue-600">(El mínimo tributable = Multiplicador × Valor UC)</span>
                  </p>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Nro. LAE</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">RIF</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Nombre/Razón Social</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Actividad Económica</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Ingreso Bruto</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Alícuota</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Imp. Calculado</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Mult.</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Mín. Aplicado</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Mes/Año</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDeclaracionesMinimo.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-8 text-slate-400">
                          No se encontraron declaraciones con mínimo tributable aplicado
                        </td>
                      </tr>
                    ) : (
                      filteredDeclaracionesMinimo.map((decl, index) => (
                        <tr key={`${decl.declaracion_id}-${decl.codigo_actividad}-${index}`} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm font-medium text-amber-700">{decl.numero_lae || '-'}</td>
                          <td className="py-3 px-4 text-sm font-medium text-slate-900">{decl.rif}</td>
                          <td className="py-3 px-4 text-sm text-slate-700">{decl.nombre}</td>
                          <td className="py-3 px-4 text-sm text-slate-600 max-w-xs truncate" title={decl.actividad_economica}>
                            {decl.actividad_economica}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-slate-700">
                            Bs. {(decl.ingreso_bruto || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-slate-600">
                            {(decl.alicuota || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}%
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-red-500">
                            Bs. {(decl.impuesto_calculado || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              ×{decl.minimo_multiplicador || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-amber-600">
                            Bs. {(decl.minimo_tributable || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              {decl.mes_anio}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-sm text-slate-500">
                Mostrando {filteredDeclaracionesMinimo.length} de {reporteData.declaraciones?.length || 0} declaraciones
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchMinimoTributableData(false)} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default MinimoTributable;
